<template>
  <div class="page">
    <div
      class="back-link"
      @click="$router.push({ name: 'Data' })"
    >
      <b-icon-chevron-left font-scale="1.3" />
      <span>
        {{ $t('datasets.data') }}
      </span>
    </div>
    <div>
      <div class="top">
        <b-row>
          <b-col class="left-col">
            <span>
              <img
                src="@/assets/icons/files_ressources.svg"
                alt="Icon files resources"
              >
              <div
                v-if="loadingDatasetsList"
                class="spinner-grow spinner-grow-sm"
                role="status"
              />
              <span v-else>{{ datasetsCount }}</span> 
              {{ $tc('words.dataset', datasetsCount) }}
            </span>
          </b-col>
          <b-col class="right-col vertical">
            <button
              class="export-button"
              @click="exportDatasetToODL"
            >
              {{ capitalizeFirstLetter($t('words.export')) }}
              <i class="bi bi-file-earmark-arrow-down" />
            </button>
          </b-col>
        </b-row>
      </div>
      <div class="content">
        <SwitchButton
          v-if="userData.is_superuser"
          :active="{
            label: $t('datasets.seeAll'),
            value: null
          }"
          :disabled="{
            label: $t('datasets.seeAll'),
            value: 'change_dataset'
          }"
          :initial-value="isSeeAllChecked"
          @toggle="setSeeAllFilter"
        />
        <div class="row">
          <div class="col-3">
            <DatasetsFilters
              @set="setDatasetsFilters"
            />
          </div>
          <div class="col-9">
            <DatasetsList
              :loading="loadingDatasetsList"
              @loading="$event => loadingDatasetsList = $event"
              @sort-by="$event => sortBy = $event"
              @get-data="getData"
              @change-page="changePage"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DatasetsFilters from '@/app/dataset/components/DatasetsFilters.vue';
import DatasetsList from '@/app/dataset/components/DatasetsList.vue';
import SwitchButton from '@/components/Elements/SwitchButton.vue';

import { mapState, mapMutations, mapActions } from 'vuex';

export default {
  name: 'DatasetsView',

  components: {
    DatasetsFilters,
    DatasetsList,
    SwitchButton
  },

  data() {
    return {
      initiating: true,
      loadingDatasetsList: false,

      isSeeAllChecked: false,
      sortBy: {
        direction: '',
        field: ''
      },

      datasetToDelete: null,
      isDeleteDatasetWithResourceOpen: false,
      datasetWithResourceModalData: {
        name: null,
        nbResources: null
      }
    };
  },

  computed: {
    ...mapState('login', [
      'userData'
    ]),
    ...mapState('datasets', [
      'currentDataset',
      'datasetsList',
      'datasetsCount',
      'isDatasetsListSearched',
      'areAllDatasetsVisible'
    ]),
    ...mapState('table', [
      'currentTables'
    ]),
  },

  watch: {
    sortBy: {
      deep: true,
      handler(newValue, oldValue) {
        if (newValue !== oldValue) {
          this.getData();
        }
      }
    }
  },

  beforeRouteEnter(to, from, next) {
    next((vm) => {
      if (from.name === 'DatasetPermissions') {
        vm.RESET_USERGROUPS_PERMISSIONS_FILTERS('usergroup_type');
        vm.RESET_USERGROUPS_PERMISSIONS_FILTERS('organisation_type_id');
        vm.RESET_USERGROUPS_PERMISSIONS_FILTERS('level');
      }
    });
  },

  created() {
    this.RESET_DATASETS_FILTERS();
    if (!this.userData.is_superuser || (this.userData.is_superuser && !this.areAllDatasetsVisible)) {
      this.isSeeAllChecked = false;
      this.SET_DATASETS_FILTERS({
        filter: 'perms',
        value: 'change_dataset'
      });
    } else if (this.userData.is_superuser && this.areAllDatasetsVisible) {
      this.isSeeAllChecked = true;
      this.SET_DATASETS_FILTERS({
        filter: 'perms',
        value: null
      });
    }
    // Clean stored form
    this.SET_CURRENT_FORM({
      id: null,
      form: null
    });
  },

  mounted() {
    // Watch modal closing event
    this.$root.$on('bv::modal::hide', () => {
      this.isDeleteDatasetWithResourceOpen = false;
    });

    this.initiating = false;
  },

  beforeRouteLeave(to, from, next) {
    this.RESET_DATASETS_FILTERS();
    next();
  },

  methods: {
    ...mapMutations('table', [
      'SET_TABLE'
    ]),
    ...mapMutations('form', [
      'SET_CURRENT_FORM'
    ]),
    ...mapMutations('datasets', [
      'RESET_USERGROUPS_PERMISSIONS_FILTERS',
      'SET_ALL_DATASETS_VISIBLE',
      'SET_DATASETS_FILTERS',
      'RESET_DATASETS_FILTERS',
    ]),
    ...mapActions('datasets', [
      'GET_DATASETS_LIST',
      'EXPORT_DATASET_LIST'
    ]),

    async getData(page) {
      try {
        this.loadingDatasetsList = true;
        await this.GET_DATASETS_LIST({ ...this.sortBy, page: page });
        this.loadingDatasetsList = false;
      } catch (err) {
        console.error(err);
        if (err && err.code && err.code !== 'ERR_CANCELED') {
          this.loadingDatasetsList = false;
        }
      }
    },

    changePage(e) {
      this.getData(e);
    },

    setSeeAllFilter(value) {
      this.SET_ALL_DATASETS_VISIBLE(value === null ? true : false);
      this.setDatasetsFilters({
        filter: 'perms',
        value: value
      });
    },

    setDatasetsFilters(e) {
      this.SET_DATASETS_FILTERS({
        filter: e.filter,
        value: e.value
      });
      if (!this.initiating) {
        this.getData();
      }
    },

    async exportDatasetToODL() {
      this.loadingDatasetsList = true;
      await this.EXPORT_DATASET_LIST();
      this.loadingDatasetsList = false;
    }
  }
};
</script>

<style lang="less" scoped>
.datasets-sort-selector {
  width: max-content;
  min-width: 245px;
}
.content {
  margin: 1rem 2.5rem 1rem 2.4rem;
  padding-right: 5px;
}
</style>
